// @flow
/* eslint-disable rover/no-platform-specific-globals-or-imports */
import bowser from 'bowser';
import type { $Request } from 'express';

import type { ExpressParams } from '@rover/react-lib/types';

type UtilFunction = (userAgentString?: ?string) => boolean;

// this return type stinks but otherwise each function
// has to coerce its return type to a boolean rather than
// just doing the normal JS thing. UtilFunction ends
// up being the exposed one anyway so we still get a
// (relatively) clean public API anyway.
type BowserFunction = (?string) => ?(boolean | '');

export const MOBILE_EMBEDDED_HEADER = 'HTTP_X_MOBILE_EMBEDDED';
export const MOBILE_EMBEDDED_COOKIE = 'mobile-embedded';
export const EMBEDDED_WEB_CLASS_NAME = 'js-mobile-embedded';

export const BOT_USER_AGENTS = [
  // Taken from https://github.com/selwin/python-user-agents/blob/master/user_agents/devices.json
  'Mozilla/5.0 (compatible; Googlebot/2.1; +http://www.google.com/bot.html)',
  'Mozilla/5.0 (iPhone; U; CPU iPhone OS) (compatible; Googlebot-Mobile/2.1; http://www.google.com/bot.html)',
  'Mozilla/5.0 (iPhone; CPU iPhone OS 6_0 like Mac OS X) AppleWebKit/536.26 (KHTML, like Gecko) Version/6.0 Mobile/10A5376e Safari/8536.25 (compatible; Googlebot/2.1; +http://www.google.com/bot.html)',
  'DoCoMo/2.0 N905i(c100;TB;W24H16) (compatible; Googlebot-Mobile/2.1; +http://www.google.com/bot.html)',
];

export const isWindowNavigatorUserAgentDefined = (): boolean => {
  if (typeof window === 'undefined') return false;
  const global = window;
  return (
    global &&
    global.navigator &&
    typeof global.navigator.userAgent === 'string' &&
    global.navigator.userAgent.length > 0
  );
};

export const getUserAgentFromExpressParams = (expressParams: ?ExpressParams): ?string => {
  if (expressParams) {
    const { req } = expressParams;
    return req.get('User-Agent') || null;
  }
  return isWindowNavigatorUserAgentDefined() ? window.navigator.userAgent : null;
};

// Takes in a BowserFunction and makes it safe to call with or without a string arg or the window present
export const safeWindowFunc = (bowserFuncToWrap: BowserFunction): UtilFunction => (
  userAgentString: ?string
): boolean => {
  if (userAgentString) {
    return !!bowserFuncToWrap(userAgentString);
  } else if (isWindowNavigatorUserAgentDefined()) {
    return !!bowserFuncToWrap(window.navigator.userAgent);
  }

  return !!bowserFuncToWrap(null);
};

// bowser-based functions
export const isAndroid: UtilFunction = safeWindowFunc(
  (userAgentString: ?string) =>
    userAgentString && bowser.getParser(userAgentString).parsedResult.os.name === 'Android'
);

export const isWindowsPhone: UtilFunction = safeWindowFunc(
  (userAgentString: ?string) =>
    userAgentString && bowser.getParser(userAgentString).parsedResult.os.name === 'Windows Phone'
);

export const isKindle: UtilFunction = safeWindowFunc(
  (userAgentString: ?string) =>
    userAgentString && bowser.getParser(userAgentString).parsedResult.platform.vendor === 'Amazon'
);

export const isiOS: UtilFunction = safeWindowFunc(
  (userAgentString: ?string) =>
    userAgentString && bowser.getParser(userAgentString).parsedResult.os.name === 'iOS'
);

export const isiOS9: UtilFunction = safeWindowFunc((userAgentString: ?string) => {
  if (!userAgentString) {
    return false;
  }

  const { parsedResult } = bowser.getParser(userAgentString);
  return (
    parsedResult.os.name === 'iOS' &&
    typeof parsedResult.os.version === 'string' &&
    parsedResult.os.version.startsWith('9.')
  );
});

export const isMobileBrowser: UtilFunction = safeWindowFunc(
  (userAgentString: ?string) =>
    userAgentString && bowser.getParser(userAgentString).parsedResult.platform.type === 'mobile'
);

export const isTabletBrowser: UtilFunction = safeWindowFunc(
  (userAgentString: ?string) =>
    userAgentString && bowser.getParser(userAgentString).parsedResult.platform.type === 'tablet'
);

export const isDesktopBrowser: UtilFunction = safeWindowFunc(
  (userAgentString: ?string) =>
    userAgentString && bowser.getParser(userAgentString).parsedResult.platform.type === 'desktop'
);

export const isIE: UtilFunction = safeWindowFunc(
  (userAgentString: ?string) =>
    userAgentString &&
    bowser.getParser(userAgentString).parsedResult.browser.name === 'Internet Explorer'
);

export const isIE11: UtilFunction = safeWindowFunc((userAgentString: ?string) => {
  if (!userAgentString) {
    return false;
  }

  const { parsedResult } = bowser.getParser(userAgentString);
  return (
    parsedResult.browser.name === 'Internet Explorer' &&
    typeof parsedResult.browser.version === 'string' &&
    parsedResult.browser.version.startsWith('11.')
  );
});

export const isEdge: UtilFunction = safeWindowFunc(
  (userAgentString: ?string) =>
    userAgentString &&
    bowser.getParser(userAgentString).parsedResult.browser.name === 'Microsoft Edge'
);

export const isChrome: UtilFunction = safeWindowFunc(
  (userAgentString: ?string) =>
    userAgentString && bowser.getParser(userAgentString).parsedResult.browser.name === 'Chrome'
);

export const isSafari: UtilFunction = safeWindowFunc(
  (userAgentString: ?string) =>
    userAgentString && bowser.getParser(userAgentString).parsedResult.browser.name === 'Safari'
);

export const isAndroidChrome: UtilFunction = safeWindowFunc(
  (userAgentString: ?string) => isAndroid(userAgentString) && isChrome(userAgentString)
);

export const isIosSafari: UtilFunction = safeWindowFunc(
  (userAgentString: ?string) => isiOS(userAgentString) && isSafari(userAgentString)
);

export const isBot: UtilFunction = (userAgentString?: ?string) =>
  BOT_USER_AGENTS.includes(userAgentString);

export const isMobileEmbedded = (): boolean => {
  const doc = document;
  return doc && doc.body ? doc.body.classList.contains(EMBEDDED_WEB_CLASS_NAME) : false;
};

export const isHoverable = (): boolean => {
  const global = window;
  return global && global.matchMedia ? !global.matchMedia('(hover: none)').matches : false;
};

// Only use server-side
export const isExpressRequestMobileEmbedded = (req: $Request): boolean =>
  !!req.header(MOBILE_EMBEDDED_HEADER) ||
  (req.get('cookie') && req.get('cookie').includes(MOBILE_EMBEDDED_COOKIE));

// https://stackoverflow.com/a/39473604
export const isReactNative = (passedDocument?: any): boolean => {
  let doc = passedDocument;

  if (!doc && typeof window === 'object') {
    doc = window.document;
  }
  if (!doc) {
    return false;
  }
  return typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
};



// WEBPACK FOOTER //
// ../utilities/userAgent.js